// scenes/.../video_renderpipeline.ts

import RenderPipeline from "../../../lib/services/render/renderpipeline";
import ShaderService from "../../../lib/services/shader/shader_service";
import ResourceService from "../../../lib/services/resource/resourceservice";
import ColorCameraSlot from "../../../lib/services/capture/color_camera_slot";

export default class VideoRenderPipeline extends RenderPipeline {
  label: string;
  shader_name: string;

  // GPU state.
  sampler: GPUSampler | null = null;

  // Bind group references the currently bound slot's views.
  bind_group: GPUBindGroup | null = null;

  // Track format of the slot we built the bind group layout for. NV12 has 2
  // textures, I420 has 3 — we rebuild the bind group accordingly.
  private bound_format: "NV12" | "I420" | null = null;
  private params_buffer: GPUBuffer | null = null;

  constructor(key: string, shader_name: string) {
    super(key + "_video");
    this.label = key + "_video";
    this.shader_name = shader_name;
  }

    update_params(
      device: GPUDevice,
      mode: number,                 // 0=none, 1=mirrorX, 2=rotCW, 3=rotCW+mirrorX, 4=rotCCW, 5=rotCCW+mirrorX
      src_width: number,
      src_height: number,
      dst_width: number,
      dst_height: number
    ): void {
      if (!this.params_buffer) return;
      const buf = new ArrayBuffer(32);
      const u32 = new Uint32Array(buf);
      const f32 = new Float32Array(buf);
      u32[0] = mode;
      // u32[1..3] padding
      f32[4] = src_width;
      f32[5] = src_height;
      f32[6] = dst_width;
      f32[7] = dst_height;
      device.queue.writeBuffer(this.params_buffer, 0, buf);
  }

  async build(
    device: GPUDevice,
    format: GPUTextureFormat,
    shader_service: ShaderService,
    _resource_service: ResourceService
  ): Promise<void> {
    const shader = shader_service.get_render(this.shader_name);

      this.params_buffer = device.createBuffer({
      label: this.label + "_params",
      size: 32,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });

    // Same bind group layout for both NV12 and I420; the third texture
    // binding is only sampled when the shader is compiled with the
    // matching variant. To keep the pipeline single-purpose, we'll go
    // with two slightly different shaders for NV12 vs I420. For now,
    // assume NV12 (most cameras on iOS/Mac). Trivial to extend.

    this.pipeline = device.createRenderPipeline({
      layout: "auto",
      label: this.label,
      vertex: {
        module: shader,
        entryPoint: "vs_main",
        buffers: [],   // full-screen triangle generated in the shader
      },
      fragment: {
        module: shader,
        entryPoint: "fs_main",
        targets: [
          {
            format,
            blend: {
              color: {
                operation: "add",
                srcFactor: "one",
                dstFactor: "zero",
              },
              alpha: {
                operation: "add",
                srcFactor: "one",
                dstFactor: "zero",
              },
            },
          },
        ],
      },
      primitive: {
        topology: "triangle-list",
        cullMode: "none",
      },
      depthStencil: {
        format: "depth24plus",
        depthWriteEnabled: false,
        depthCompare: "always", // overlay everything
      },
    });

    this.sampler = device.createSampler({
      label: this.label + "_sampler",
      magFilter: "linear",
      minFilter: "linear",
      addressModeU: "clamp-to-edge",
      addressModeV: "clamp-to-edge",
    });
  }

  /**
   * Update the bind group to sample from the given slot's textures.
   * Called whenever the source slot identity changes.
   */
  bind_slot(device: GPUDevice, slot: ColorCameraSlot): void {
  if (!this.pipeline || !this.sampler || !this.params_buffer) return;
  if (!slot.y_view) return;

  const entries: GPUBindGroupEntry[] = [
    { binding: 0, resource: { buffer: this.params_buffer } },
    { binding: 1, resource: this.sampler },
    { binding: 2, resource: slot.y_view },
  ];

  if (slot.format === "NV12") {
    if (!slot.cbcr_view) return;
    entries.push({ binding: 3, resource: slot.cbcr_view });
  } else {
    if (!slot.u_view || !slot.v_view) return;
    entries.push({ binding: 3, resource: slot.u_view });
    entries.push({ binding: 4, resource: slot.v_view });
  }

  this.bind_group = device.createBindGroup({
    layout: this.pipeline.getBindGroupLayout(0),
    label: this.label + "_bind_group",
    entries,
  });

  this.bound_format = slot.format as "NV12" | "I420";
}

  prepare(_device: GPUDevice): void {
    // No per-frame buffer writes; sampler + texture views are bound once
    // per slot change.
  }

  draw(pass: GPURenderPassEncoder): void {
    if (!this.pipeline || !this.bind_group) return;

    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, this.bind_group);
    pass.draw(6);  // full-screen triangle
  }
}